import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { Plus, Edit, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import {
  fetchAllTestimonials,
  createTestimonial,
  updateTestimonial,
  deleteTestimonial,
  type Testimonial
} from "@/services/testimonialsService";

interface TestimonialFormData {
  name: string;
  handle: string;
  avatar_url: string;
  text: string;
  href?: string;
  is_active: boolean;
  display_order: number;
}

export default function DepoimentosAdmin() {
  const [selectedTestimonial, setSelectedTestimonial] = useState<Testimonial | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<TestimonialFormData>({
    name: "",
    handle: "",
    avatar_url: "",
    text: "",
    href: "",
    is_active: true,
    display_order: 0
  });

  const queryClient = useQueryClient();

  const { data: testimonials = [], isLoading } = useQuery({
    queryKey: ["all-testimonials"],
    queryFn: fetchAllTestimonials,
  });

  const createMutation = useMutation({
    mutationFn: createTestimonial,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-testimonials"] });
      queryClient.invalidateQueries({ queryKey: ["active-testimonials"] });
      setIsDialogOpen(false);
      resetForm();
      toast.success("Depoimento criado com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao criar depoimento");
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Testimonial> }) =>
      updateTestimonial(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-testimonials"] });
      queryClient.invalidateQueries({ queryKey: ["active-testimonials"] });
      setIsDialogOpen(false);
      setSelectedTestimonial(null);
      resetForm();
      toast.success("Depoimento atualizado com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao atualizar depoimento");
    }
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTestimonial,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-testimonials"] });
      queryClient.invalidateQueries({ queryKey: ["active-testimonials"] });
      toast.success("Depoimento deletado com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao deletar depoimento");
    }
  });

  const resetForm = () => {
    setFormData({
      name: "",
      handle: "",
      avatar_url: "",
      text: "",
      href: "",
      is_active: true,
      display_order: 0
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedTestimonial) {
      updateMutation.mutate({
        id: selectedTestimonial.id,
        updates: formData
      });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (testimonial: Testimonial) => {
    setSelectedTestimonial(testimonial);
    setFormData({
      name: testimonial.name,
      handle: testimonial.handle,
      avatar_url: testimonial.avatar_url,
      text: testimonial.text,
      href: testimonial.href || "",
      is_active: testimonial.is_active,
      display_order: testimonial.display_order
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm("Tem certeza que deseja deletar este depoimento?")) {
      deleteMutation.mutate(id);
    }
  };

  const toggleActive = (testimonial: Testimonial) => {
    updateMutation.mutate({
      id: testimonial.id,
      updates: { is_active: !testimonial.is_active }
    });
  };

  const moveTestimonial = (testimonial: Testimonial, direction: 'up' | 'down') => {
    const newOrder = direction === 'up' 
      ? testimonial.display_order - 1 
      : testimonial.display_order + 1;
    
    updateMutation.mutate({
      id: testimonial.id,
      updates: { display_order: newOrder }
    });
  };

  if (isLoading) {
    return <div className="p-8 text-center">Carregando depoimentos...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-white">Gerenciar Depoimentos</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => {
                setSelectedTestimonial(null);
                resetForm();
              }}
              className="bg-[#00B6F0] hover:bg-[#00B6F0]/80"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Depoimento
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-[#1A2233] border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white">
                {selectedTestimonial ? "Editar Depoimento" : "Novo Depoimento"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-white">Nome</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="handle" className="text-white">Handle (@username)</Label>
                  <Input
                    id="handle"
                    value={formData.handle}
                    onChange={(e) => setFormData({...formData, handle: e.target.value})}
                    placeholder="@username"
                    required
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="avatar_url" className="text-white">URL do Avatar</Label>
                <Input
                  id="avatar_url"
                  value={formData.avatar_url}
                  onChange={(e) => setFormData({...formData, avatar_url: e.target.value})}
                  placeholder="https://..."
                  className="bg-slate-800 border-slate-600 text-white"
                />
              </div>

              <div>
                <Label htmlFor="text" className="text-white">Texto do Depoimento</Label>
                <Textarea
                  id="text"
                  value={formData.text}
                  onChange={(e) => setFormData({...formData, text: e.target.value})}
                  required
                  rows={4}
                  className="bg-slate-800 border-slate-600 text-white"
                />
              </div>

              <div>
                <Label htmlFor="href" className="text-white">Link (opcional)</Label>
                <Input
                  id="href"
                  value={formData.href}
                  onChange={(e) => setFormData({...formData, href: e.target.value})}
                  placeholder="https://..."
                  className="bg-slate-800 border-slate-600 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  />
                  <Label htmlFor="is_active" className="text-white">Ativo</Label>
                </div>
                <div>
                  <Label htmlFor="display_order" className="text-white">Ordem de Exibição</Label>
                  <Input
                    id="display_order"
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value)})}
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsDialogOpen(false)}
                  className="border-slate-600 text-white"
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#00B6F0] hover:bg-[#00B6F0]/80"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {selectedTestimonial ? "Atualizar" : "Criar"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {testimonials.map((testimonial) => (
          <Card key={testimonial.id} className="bg-[#1A2233] border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={testimonial.avatar_url} alt={testimonial.name} />
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-semibold text-white">{testimonial.name}</h3>
                      <span className="text-[#00B6F0] text-sm">{testimonial.handle}</span>
                      <Badge variant={testimonial.is_active ? "default" : "secondary"}>
                        {testimonial.is_active ? "Ativo" : "Inativo"}
                      </Badge>
                    </div>
                    <p className="text-slate-300 mt-2">{testimonial.text}</p>
                    {testimonial.href && (
                      <a 
                        href={testimonial.href} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#00B6F0] text-sm hover:underline"
                      >
                        Ver perfil →
                      </a>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <div className="flex flex-col space-y-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => moveTestimonial(testimonial, 'up')}
                      className="text-white hover:bg-slate-700"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <span className="text-xs text-slate-400 text-center">{testimonial.display_order}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => moveTestimonial(testimonial, 'down')}
                      className="text-white hover:bg-slate-700"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <Switch
                    checked={testimonial.is_active}
                    onCheckedChange={() => toggleActive(testimonial)}
                  />
                  
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEdit(testimonial)}
                    className="text-[#00B6F0] hover:bg-slate-700"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(testimonial.id)}
                    className="text-red-400 hover:bg-slate-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {testimonials.length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate-400 text-lg">Nenhum depoimento cadastrado</p>
          <p className="text-slate-500 mt-2">Clique no botão "Novo Depoimento" para começar</p>
        </div>
      )}
    </div>
  );
} 